import { useState, useEffect, useCallback, useRef } from 'react'
import { cajaApi, cuentasContablesApi } from '../api/caja'
import { fmtMonto, parseMonto, fmtFecha } from '../utils/fecha'
import CampoFecha from './CampoFecha'

const hoyISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Pantalla de caja parametrizada por tipo: 'pago' (haber) | 'cobranza' (debe)
export default function RegistroCaja({ tipo }) {
  const esCobranza = tipo === 'cobranza'
  const titulo = esCobranza ? 'Cobranzas' : 'Pagos varios'

  const [fecha, setFecha] = useState(hoyISO())
  const [cuentas, setCuentas] = useState([])
  const [favoritos, setFavoritos] = useState([])
  const [movs, setMovs] = useState([])
  const [minuta, setMinuta] = useState(null)
  const [total, setTotal] = useState(0)

  const [cuentaId, setCuentaId] = useState('')
  const [cuentaCodigo, setCuentaCodigo] = useState('')
  const [concepto, setConcepto] = useState('')
  const [importe, setImporte] = useState('')
  const [cliCodigo, setCliCodigo] = useState('')
  const [vendedor, setVendedor] = useState('')

  const [guardando, setGuardando] = useState(false)
  const [cerrando, setCerrando] = useState(false)
  const [msg, setMsg] = useState(null)
  const [gestor, setGestor] = useState(false)
  const [buscador, setBuscador] = useState(false)
  const importeRef = useRef(null)

  const cargarCuentas = useCallback(async () => {
    const { data } = await cuentasContablesApi.listar({ activas: 1, tipo })
    setCuentas(data)
  }, [tipo])
  const cargarFavoritos = useCallback(async () => {
    const { data } = await cajaApi.favoritos(tipo)
    setFavoritos(data)
  }, [tipo])
  const cargarMovs = useCallback(async () => {
    const { data } = await cajaApi.movimientos(tipo, fecha)
    setMovs(data.movimientos || [])
    setMinuta(data.minuta || null)
    setTotal(data.total || 0)
  }, [tipo, fecha])

  useEffect(() => { cargarCuentas(); cargarFavoritos() }, [cargarCuentas, cargarFavoritos])
  useEffect(() => { cargarMovs() }, [cargarMovs])

  const cuentaSel = cuentas.find(c => String(c.id) === cuentaId)

  // Selección por CÓDIGO: al tipear, si coincide con una cuenta activa, queda elegida.
  const onCodigo = (val) => {
    const v = val.toUpperCase()
    setCuentaCodigo(v)
    const m = cuentas.find(c => String(c.codigo).toUpperCase() === v.trim())
    setCuentaId(m ? String(m.id) : '')
  }
  const elegirCuenta = (c) => {
    setCuentaId(String(c.id))
    setCuentaCodigo(c.codigo)
    setBuscador(false)
    setTimeout(() => importeRef.current?.focus(), 0)
  }

  const registrar = async () => {
    const imp = parseMonto(importe)
    if (!cuentaId) { setMsg({ tipo: 'err', txt: 'Elegí una cuenta por código (o usá el buscador)' }); return }
    if (isNaN(imp) || imp === 0) { setMsg({ tipo: 'err', txt: 'Importe inválido (puede ser negativo, no cero)' }); return }
    setGuardando(true); setMsg(null)
    try {
      await cajaApi.registrar({
        tipo, fecha, cuenta_contable_id: Number(cuentaId),
        concepto: concepto || null, importe: imp,
        cliente_codigo: esCobranza ? (cliCodigo || null) : null,
        vendedor: esCobranza ? (vendedor || null) : null,
      })
      setImporte(''); setConcepto(''); setCliCodigo(''); setVendedor('')
      await cargarMovs()
      setTimeout(() => importeRef.current?.focus(), 0)
    } catch (e) {
      setMsg({ tipo: 'err', txt: e.response?.data?.message || 'No se pudo registrar' })
    } finally { setGuardando(false) }
  }

  const revertir = async (m) => {
    if (!window.confirm(`Revertir el movimiento de $ ${fmtMonto(m.importe)} (${m.codigo_cuenta})? Se crea un contra-asiento y el original queda marcado como REVERTIDO. Solo se puede revertir una vez.`)) return
    try { await cajaApi.revertirMovimiento(m.id); await cargarMovs() }
    catch (e) { setMsg({ tipo: 'err', txt: e.response?.data?.message || 'No se pudo revertir' }) }
  }

  const cerrar = async () => {
    if (!minuta) return
    if (!window.confirm(`Cerrar la minuta de pagos del ${fmtFecha(fecha)} y enviarla a Presea?`)) return
    setCerrando(true); setMsg(null)
    try {
      const { data } = await cajaApi.cerrarMinuta(minuta.id)
      setMsg(data.estado === 'enviada'
        ? { tipo: 'ok', txt: `Minuta cerrada y enviada a Presea (${data.presea_ref || 'ok'})` }
        : { tipo: 'err', txt: `Minuta cerrada pero el envío falló: ${data.error_envio || 'error'}. Reintentá desde Minutas.` })
      await cargarMovs()
    } catch (e) {
      setMsg({ tipo: 'err', txt: e.response?.data?.message || 'No se pudo cerrar' })
    } finally { setCerrando(false) }
  }

  const onKeyImporte = (e) => { if (e.key === 'Enter') { e.preventDefault(); registrar() } }
  // En el campo Código, Tab o Enter saltan directo a Importe.
  const onKeyCodigo = (e) => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); importeRef.current?.focus() } }

  return (
    <div style={st.wrap}>
      <div style={st.head}>
        <h1 style={st.h1}>{esCobranza ? '📥' : '📤'} {titulo} {esCobranza ? '(debe)' : '(haber)'}</h1>
        <div style={st.fechaBox}>
          <label style={st.lbl}>Fecha</label>
          <CampoFecha style={st.input} value={fecha} onChange={setFecha} data-nocaps />
        </div>
      </div>

      {/* Botonera de favoritos */}
      <div style={st.card}>
        <div style={st.favHead}>
          <span style={st.favTit}>Cuentas frecuentes</span>
          <button style={st.btnGhost} onClick={() => setGestor(true)}>⚙ Configurar botonera</button>
        </div>
        <div style={st.favBar}>
          {favoritos.length === 0 && <span style={st.muted}>Sin favoritos. Configurá tu botonera para cargar más rápido.</span>}
          {favoritos.map(f => f.cuenta && (
            <button key={f.id}
              onClick={() => elegirCuenta(f.cuenta)}
              style={{ ...st.favBtn, ...(String(f.cuenta.id) === cuentaId ? st.favBtnSel : {}) }}>
              {f.cuenta.nombre}
            </button>
          ))}
        </div>
      </div>

      {/* Carga rápida */}
      <div style={st.card}>
        <div style={st.formRow}>
          {esCobranza && (
            <>
              <div style={{ flex: '0 0 120px' }}>
                <label style={st.lbl}>Cód. cliente</label>
                <input style={st.input} value={cliCodigo} onChange={e => setCliCodigo(e.target.value)} />
              </div>
              <div style={{ flex: '0 0 150px' }}>
                <label style={st.lbl}>Vendedor</label>
                <input style={st.input} value={vendedor} onChange={e => setVendedor(e.target.value)} />
              </div>
            </>
          )}
          <div style={{ flex: '0 0 170px' }}>
            <label style={st.lbl}>Cuenta (código)</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input style={{ ...st.input, fontFamily: 'monospace', fontWeight: 700 }}
                value={cuentaCodigo} onChange={e => onCodigo(e.target.value)} onKeyDown={onKeyCodigo} placeholder="Código" />
              <button style={st.btnBuscar} onClick={() => setBuscador(true)} title="Buscar por número">🔍</button>
            </div>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={st.lbl}>Concepto (opcional)</label>
            <input style={st.input} value={concepto} onChange={e => setConcepto(e.target.value)} />
          </div>
          <div style={{ flex: '0 0 140px' }}>
            <label style={st.lbl}>Importe</label>
            <input ref={importeRef} style={{ ...st.input, textAlign: 'right', fontWeight: 700 }}
              value={importe} onChange={e => setImporte(e.target.value)} onKeyDown={onKeyImporte}
              placeholder="0.00" inputMode="decimal" data-nocaps />
          </div>
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end' }}>
            <button style={st.btnReg} onClick={registrar} disabled={guardando}>
              {guardando ? '...' : '＋ Registrar'}
            </button>
          </div>
        </div>
        {cuentaSel
          ? <div style={st.hintSel}>Cuenta: <b>{cuentaSel.codigo} · {cuentaSel.nombre}</b> — Enter en Importe registra</div>
          : cuentaCodigo ? <div style={st.hintErr}>No hay cuenta con ese código. Usá 🔍 para buscar por número.</div> : null}
        {msg && <div style={msg.tipo === 'ok' ? st.ok : st.err}>{msg.txt}</div>}
      </div>

      {/* Grilla del día */}
      <div style={st.card}>
        <div style={st.gridHead}>
          <span style={st.favTit}>Movimientos del {fmtFecha(fecha)}{minuta ? '' : ' (sin minuta abierta)'}</span>
          <div style={st.totBox}>
            <span style={st.muted}>Total</span>
            <span style={st.tot}>$ {fmtMonto(total)}</span>
            {!esCobranza && (
              <button style={st.btnCerrar} onClick={cerrar} disabled={!minuta || movs.length === 0 || cerrando}>
                {cerrando ? 'Cerrando...' : '🔒 Cerrar minuta del día'}
              </button>
            )}
          </div>
        </div>
        <table style={st.table}>
          <thead>
            <tr>
              <th style={st.th}>Código</th>
              <th style={st.th}>Cuenta</th>
              {esCobranza && <th style={st.th}>Cód. cliente</th>}
              {esCobranza && <th style={st.th}>Vendedor</th>}
              <th style={st.th}>Concepto</th>
              <th style={{ ...st.th, textAlign: 'right' }}>Importe</th>
              <th style={st.th}></th>
            </tr>
          </thead>
          <tbody>
            {movs.length === 0 && <tr><td colSpan={esCobranza ? 7 : 5} style={st.empty}>Sin movimientos cargados hoy.</td></tr>}
            {movs.map(m => {
              const esReversa = !!m.revierte_a_id
              const revertido = !!m.revertido
              const rowStyle = revertido ? { ...st.tr, background: '#fef2f2', color: '#94a3b8' } : (esReversa ? { ...st.tr, background: '#f8fafc' } : st.tr)
              return (
                <tr key={m.id} style={rowStyle}>
                  <td style={st.td}><code style={st.code}>{m.codigo_cuenta}</code></td>
                  <td style={st.td}>{m.nombre_cuenta}</td>
                  {esCobranza && <td style={st.td}>{m.cliente_codigo || '—'}</td>}
                  {esCobranza && <td style={st.td}>{m.vendedor || '—'}</td>}
                  <td style={st.td}>
                    {m.concepto || '—'}
                    {revertido && <span style={st.tagRevertido}>REVERTIDO</span>}
                    {esReversa && <span style={st.tagReversa}>↩ REVERSA</span>}
                  </td>
                  <td style={{ ...st.td, textAlign: 'right', fontWeight: 700, textDecoration: revertido ? 'line-through' : 'none', color: revertido ? '#94a3b8' : (Number(m.importe) < 0 ? '#b91c1c' : '#166534') }}>$ {fmtMonto(m.importe)}</td>
                  <td style={{ ...st.td, textAlign: 'right' }}>
                    {(!esReversa && !revertido)
                      ? <button style={st.btnRev} onClick={() => revertir(m)} title="Revertir (contra-asiento, una sola vez)">↩ Revertir</button>
                      : <span style={st.muted}>—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {gestor && (
        <GestorFavoritos tipo={tipo} cuentas={cuentas} favoritos={favoritos}
          onClose={() => setGestor(false)} onChange={cargarFavoritos} />
      )}
      {buscador && (
        <BuscadorCuenta cuentas={cuentas} onPick={elegirCuenta} onClose={() => setBuscador(false)} />
      )}
    </div>
  )
}

function BuscadorCuenta({ cuentas, onPick, onClose }) {
  const [q, setQ] = useState('')
  const lista = cuentas.filter(c => `${c.codigo} ${c.nombre}`.toLowerCase().includes(q.toLowerCase()))
  return (
    <div style={st.modalBg}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <div style={st.modalHead}>
          <h3 style={{ margin: 0 }}>Buscar cuenta por número</h3>
          <button style={st.btnGhost} onClick={onClose}>Cerrar</button>
        </div>
        <input autoFocus style={{ ...st.input, marginBottom: 10 }} placeholder="Número o nombre..." value={q} onChange={e => setQ(e.target.value)} />
        <div style={st.favList}>
          {lista.map(c => (
            <button key={c.id} onClick={() => onPick(c)} style={st.favItem}>
              <span><code style={st.code}>{c.codigo}</code> {c.nombre}</span>
            </button>
          ))}
          {lista.length === 0 && <div style={st.empty}>Sin coincidencias. Cargá cuentas en Maestros → Cuentas contables.</div>}
        </div>
      </div>
    </div>
  )
}

function GestorFavoritos({ tipo, cuentas, favoritos, onClose, onChange }) {
  const [q, setQ] = useState('')
  const favSet = new Map(favoritos.map(f => [String(f.cuenta?.id ?? f.cuenta_contable_id), f.id]))
  const lista = cuentas.filter(c => `${c.codigo} ${c.nombre}`.toLowerCase().includes(q.toLowerCase()))
  const toggle = async (c) => {
    const favId = favSet.get(String(c.id))
    if (favId) await cajaApi.quitarFavorito(favId)
    else await cajaApi.agregarFavorito(c.id, tipo)
    await onChange()
  }
  return (
    <div style={st.modalBg}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <div style={st.modalHead}>
          <h3 style={{ margin: 0 }}>Configurar botonera ({tipo === 'cobranza' ? 'Cobranzas' : 'Pagos varios'})</h3>
          <button style={st.btnGhost} onClick={onClose}>Cerrar</button>
        </div>
        <input style={{ ...st.input, marginBottom: 10 }} placeholder="Buscar cuenta..." value={q} onChange={e => setQ(e.target.value)} />
        <div style={st.favList}>
          {lista.map(c => {
            const fav = favSet.has(String(c.id))
            return (
              <button key={c.id} onClick={() => toggle(c)} style={{ ...st.favItem, ...(fav ? st.favItemOn : {}) }}>
                <span>{fav ? '★' : '☆'} <code style={st.code}>{c.codigo}</code> {c.nombre}</span>
                <span style={st.muted}>{fav ? 'Quitar' : 'Agregar'}</span>
              </button>
            )
          })}
          {lista.length === 0 && <div style={st.empty}>No hay cuentas. Cargalas en Maestros → Cuentas contables.</div>}
        </div>
      </div>
    </div>
  )
}

const st = {
  wrap: { padding: '1.25rem', maxWidth: 1100, margin: '0 auto' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap', gap: 10 },
  h1: { margin: 0, fontSize: '1.4rem', color: '#1e293b' },
  fechaBox: { display: 'flex', flexDirection: 'column', gap: 3 },
  card: { background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  favHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  favTit: { fontWeight: 700, color: '#334155', fontSize: '0.95rem' },
  favBar: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  favBtn: { padding: '0.5rem 0.85rem', border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
  favBtnSel: { background: '#4f46e5', color: 'white', borderColor: '#4f46e5' },
  codeFav: { background: 'rgba(0,0,0,0.08)', padding: '0 4px', borderRadius: 4, fontSize: '0.75rem' },
  formRow: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' },
  lbl: { display: 'block', fontSize: '0.72rem', color: '#64748b', marginBottom: 3, fontWeight: 600 },
  input: { width: '100%', padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: '0.9rem', boxSizing: 'border-box' },
  btnBuscar: { flex: '0 0 auto', padding: '0 0.7rem', border: '1px solid #cbd5e1', background: '#f1f5f9', borderRadius: 7, cursor: 'pointer' },
  btnReg: { padding: '0.55rem 1.1rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' },
  btnGhost: { padding: '0.4rem 0.7rem', background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 7, cursor: 'pointer', fontSize: '0.8rem', color: '#475569' },
  hintSel: { marginTop: 8, fontSize: '0.8rem', color: '#475569' },
  hintErr: { marginTop: 8, fontSize: '0.8rem', color: '#b45309' },
  ok: { marginTop: 8, padding: '0.5rem 0.7rem', background: '#dcfce7', color: '#166534', borderRadius: 7, fontSize: '0.85rem' },
  err: { marginTop: 8, padding: '0.5rem 0.7rem', background: '#fee2e2', color: '#991b1b', borderRadius: 7, fontSize: '0.85rem' },
  gridHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 },
  totBox: { display: 'flex', alignItems: 'center', gap: 12 },
  tot: { fontSize: '1.2rem', fontWeight: 800, color: '#1e293b' },
  btnCerrar: { padding: '0.5rem 0.9rem', background: '#0f172a', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { textAlign: 'left', padding: '0.5rem 0.6rem', borderBottom: '2px solid #e2e8f0', color: '#475569', fontWeight: 700, fontSize: '0.78rem' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '0.5rem 0.6rem', color: '#334155' },
  code: { background: '#f1f5f9', padding: '0 4px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 700 },
  empty: { padding: '1rem', textAlign: 'center', color: '#94a3b8' },
  muted: { color: '#94a3b8', fontSize: '0.8rem' },
  btnRev: { background: 'transparent', border: '1px solid #fecaca', color: '#b91c1c', cursor: 'pointer', fontSize: '0.78rem', borderRadius: 6, padding: '0.25rem 0.5rem', fontWeight: 600 },
  tagRevertido: { marginLeft: 8, padding: '1px 7px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 800, background: '#fee2e2', color: '#991b1b', letterSpacing: '0.03em' },
  tagReversa: { marginLeft: 8, padding: '1px 7px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 800, background: '#e0e7ff', color: '#3730a3', letterSpacing: '0.03em' },
  modalBg: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal: { background: 'white', borderRadius: 12, padding: 18, width: 560, maxWidth: '100%', maxHeight: '85vh', overflow: 'auto' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  favList: { display: 'flex', flexDirection: 'column', gap: 6 },
  favItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.7rem', border: '1px solid #e2e8f0', borderRadius: 8, background: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '0.85rem' },
  favItemOn: { background: '#fffbeb', borderColor: '#fcd34d' },
}
